/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Button, Message } from 'semantic-ui-react'
import Timer from '../../../Shared/Timer/Timer'
import OrderDenyReasonSelector from '../../OrderDenyReason/OrderDenyReasonSelector'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { MOBILE_THRESHOLD } from '../../../../constants'
import { UltyModalWrapperContext } from '../../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import DefinePickupTime from './DefinePickupTime'
import PlatformModule from '../../../../services/Platform/PlatformModule'
import { addSeconds } from 'date-fns'
import { OptionBar } from './Options/OptionBar'

const statusesBeforeReady = ['RECEIVED', 'ACCEPTED', 'PREPARING']
const orderStillNotReady = (status) => statusesBeforeReady.includes(status ?? '')


const OrderFlowActions = ({
                              order,
                              orderLifetime,
                              isDenying,
                              isDenyAcceptLoading,
                              onDenyOrder,
                              onAcceptOrder,
                              onMarkAsReady,
                              onDenyReasonSubmit,
                          }) => {
    const [t] = useTranslation()
    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)
    const [, refresh] = useState(null)

    useEffect(() => {
        const timer = setInterval(() => {
            refresh(new Date())
        }, 60 * 1000)
        return () => {
            clearInterval(timer)
        }
    }, [])

    const timerEndDate = useMemo(() => {
        if (order.isScheduledForLater) {
            return null
        }

        if (order.status === 'RECEIVED') {
            return addSeconds(new Date(order.receivedAt), orderLifetime)
        }

        if ((orderStillNotReady(order.status) && order.acceptedAt)
          && order.platform.displayPreparationTimer === 'reverse') {
            const upperBound = order.isScheduledForLater
              ? new Date(order.pickupAt)
              : addSeconds(new Date(order.acceptedAt), order.acceptedOrderLifetime)
            return new Date(upperBound)
        }

        return null
    }, [order.acceptedAt, order.acceptedOrderLifetime, order.isScheduledForLater, order.pickupAt, order.platform.displayPreparationTimer, order.receivedAt, order.status, orderLifetime])

    const orderFlowDeliverooMessage = useMemo(() => {
        if (!order
          || order.platform.module !== PlatformModule.DELIVEROO
          || order.isScheduledForLater
          || order.platform.displayPreparationTimer !== 'reverse') {
            return undefined
        }
        if (order.status === 'RECEIVED') {
            return t('order.message.deliveroo_new_flow.on_received')
        }
        if (order.isModifiable) {
            return t('order.message.deliveroo_new_flow.on_accepted')
        }
    }, [order, t])

    const headerLabel = useMemo(() => {
        if (!order) return
        if (order.isScheduledForLater) {
            return (
              t('order.scheduled.order_scheduled_for', {
                  dt: new Date(order.scheduledAt).toLocaleString(localStorage.getItem('ulty-lng'), {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      hour: 'numeric',
                      minute: '2-digit',
                  }),
              })
            )
        }

        if (order.status === 'RECEIVED') {
            return t('order.an_order_has_been_received')
        }

        return ''
    }, [order, t])

    const canDeny = useMemo(() => {
        if (!order) return false

        if (order.platform?.module === PlatformModule.JUST_EATS)
            return false

        if (order.platform?.module === PlatformModule.DELIVEROO && order.platform.displayPreparationTimer === 'reverse')
            return order.status === 'RECEIVED' || (order.status === 'ACCEPTED' && order.isModifiable)

        return order.status === 'RECEIVED' || order.isScheduledForLater
    }, [order])

    const canAccept = useMemo(() => {
        return order?.status === 'RECEIVED'
    }, [order])

    const canMarkAsReady = useMemo(() => {
        return order.status !== 'RECEIVED' && orderStillNotReady(order.status)
    }, [order])

    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)
    const orButtons = isMobile ? css`
        .ui.buttons .or {
            height: .4em;
        }
    ` : css`
        .ui.buttons .or {
            height: auto;
        }
    `

    const defineDelayPickupTime = () => {
        const modalSettings = {
            size: 'tiny',
            component: <DefinePickupTime onSubmit={onAcceptOrder}/>,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    return (
      <>
          <Message css={css`
              display: flex;
              z-index: 999 !important;
              justify-content: center;
              flex-direction: column;
              align-items: center;
              margin-bottom: 1rem;
          `}>
              <Message.Header css={css`
                  font-size: 1.6em !important;
                  text-align: center;
              `}>
                  {headerLabel}
              </Message.Header>

              {!canMarkAsReady && order.isScheduledForLater && (
                <Message.Header css={css`
                    font-size: 1em !important;
                    margin-top: 1rem !important;
                    text-align: center;
                `}>
                    {t('order.scheduled.start_preparation_now')}
                </Message.Header>
              )}

              {canMarkAsReady && order.isScheduledForLater && (
                <Message.Header css={css`
                    font-size: 1em !important;
                    margin-top: 1rem !important;
                    text-align: center;
                `}>
                    <p>{t('order.scheduled.mark_as_ready_later1')}</p>
                    <p>{t('order.scheduled.mark_as_ready_later2')}</p>
                </Message.Header>
              )}

              {(orderFlowDeliverooMessage && <Message.Content css={css`
                  text-align: center;
                  font-weight: bold;
                  color: orange;
                  font-size: 1.3em;
                  white-space: pre-wrap`}>
                  <span>{orderFlowDeliverooMessage}</span>
              </Message.Content>)}

              {timerEndDate && (order.status === 'RECEIVED' || order.isModifiable) &&
                <Timer endDate={timerEndDate}/>
              }

              <div css={css`background-color: white;
                  margin: 1em;
                  ${orButtons}
              `}>
                  <Button.Group
                    vertical={isMobile}
                  >
                      {canDeny && (
                        <>
                            <Button
                              size="large"
                              color="red"
                              loading={isDenyAcceptLoading}
                              onClick={onDenyOrder}
                            >
                                {t('order.deny_more')}
                            </Button>
                            {(canAccept || canMarkAsReady) && (<Button.Or text={t('order.or')}/>)}
                        </>
                      )}
                      {(canAccept && (
                        <Button.Group
                          color="green"
                        >
                            <Button
                              size="massive"
                              loading={isDenyAcceptLoading}
                              onClick={() => onAcceptOrder()}
                              css={css`
                                  &.ui.massive.button {
                                      width: auto;
                                  }

                                  border-bottom-left-radius: ${isMobile ? '.28571429rem' : '0'} !important;
                                  border-bottom-right-radius: ${order.platform.module !== PlatformModule.UBER_EATS ? '.28571429rem' : '0'} !important;
                                  border-top-right-radius: ${order.platform.module !== PlatformModule.UBER_EATS && !isMobile ? '.28571429rem' : '0'} !important;
                                  border-top-left-radius: 0 !important;
                              `}
                            >
                                {t('order.accept')}
                            </Button>
                            {order.platform.module === PlatformModule.UBER_EATS && !order.isScheduledForLater &&
                              <Button
                                size="massive"
                                icon="stopwatch"
                                loading={isDenyAcceptLoading}
                                onClick={defineDelayPickupTime}
                                css={css`
                                    &.ui.massive.button {
                                        width: auto;
                                    }

                                    margin-left: .2em !important;

                                    border-bottom-left-radius: 0 !important;
                                    border-bottom-right-radius: .28571429rem !important;
                                    border-top-left-radius: 0 !important;
                                    border-top-right-radius: ${!isMobile ? '.28571429rem' : '0'} !important;
                                `}
                              ></Button>
                            }
                        </Button.Group>))}
                      {(canMarkAsReady && (
                        <Button.Group
                          color="green"
                        >
                            <Button
                              size="massive"
                              disabled={order.isScheduledForLater}
                              loading={isDenyAcceptLoading}
                              onClick={() => onMarkAsReady()}
                            >
                                {t('order.mark_as_ready')}
                            </Button>
                        </Button.Group>
                      ))}
                  </Button.Group>
              </div>

              <OptionBar
                orderStatus={order.status}
                pickupAt={order.pickupAt}
                isScheduledForLater={order.isScheduledForLater}
                platformModule={order.platform.module}
              />
          </Message>

          {isDenying &&
            <OrderDenyReasonSelector
              itemsFromOrder={order.orderItems}
              isSubmitting={isDenyAcceptLoading}
              onDenyReasonSubmit={onDenyReasonSubmit}
            />}
      </>
    )
}

export default OrderFlowActions
